import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { GlassPanel } from "../components/GlassPanel";
import { FormModal, Field, TextArea, SelectField } from "./LeadsPage";
import { Plus, Pencil, Trash2, Package, AlertTriangle, Minus, TrendingUp, ShoppingCart, ArrowDown, CheckCircle2, CheckSquare, Square, X } from "lucide-react";
import { toast } from "sonner";
import PurchaseMaterialModal from "../components/PurchaseMaterialModal";

const CATEGORIES = ["wrap", "ppf", "tint", "tool", "other"];
const UNITS = ["roll", "sqft", "sheet", "each"];

const blank = {
  name: "", brand: "", sku: "", category: "wrap", unit: "roll",
  unit_price: 0, stock_qty: 0, reorder_at: 0, notes: "",
};

export default function InventoryPage() {
  const [items, setItems] = useState([]);
  const [needsOrder, setNeedsOrder] = useState([]);
  const [open, setOpen] = useState(false);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  // Bulk-select state — set of material ids currently ticked.
  // Reset whenever the filter changes so "select all" never surprises
  // the user by including items hidden behind the current search.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    setItems((await api.get("/materials")).data);
    // Pull "needs to be ordered" — materials typed on job cards that don't
    // match any inventory row yet. Silent on error so the tab still works
    // for shops that haven't set up jobs yet.
    try { setNeedsOrder((await api.get("/materials/needs-ordering")).data); }
    catch (_) { setNeedsOrder([]); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...blank, ...it }); setOpen(true); };

  // Prefill the inventory form from a "needs to order" entry so office
  // staff can turn a job-card material into a stocked item in one click.
  const openAddFromNeeded = (row) => {
    setEditing(null);
    // Heuristic split: "3M 2080-G12 Deep Black Gloss" → brand=3M name=rest.
    const parts = row.material.trim().split(/\s+/);
    const brand = parts.length > 1 ? parts[0] : "";
    const name = parts.length > 1 ? parts.slice(1).join(" ") : row.material;
    setForm({ ...blank, name, brand, notes: `Requested by ${row.job_count} job${row.job_count > 1 ? "s" : ""}.` });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      unit_price: Number(form.unit_price) || 0,
      stock_qty: Number(form.stock_qty) || 0,
      reorder_at: Number(form.reorder_at) || 0,
    };
    try {
      if (editing) await api.put(`/materials/${editing.id}`, payload);
      else await api.post("/materials", payload);
      toast.success(editing ? "Material updated" : "Material added");
      setOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this material?")) return;
    await api.delete(`/materials/${id}`);
    toast.success("Deleted");
    load();
  };

  // Bulk-select helpers ---------------------------------------------------
  const toggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const clearSelection = () => setSelected(new Set());
  const toggleAllVisible = (visibleIds) => setSelected((prev) => {
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
    if (allSelected) {
      // Clear only the visible rows — keep off-screen selections intact
      const next = new Set(prev);
      visibleIds.forEach((id) => next.delete(id));
      return next;
    }
    const next = new Set(prev);
    visibleIds.forEach((id) => next.add(id));
    return next;
  });
  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} material${ids.length === 1 ? "" : "s"}? This can't be undone.`)) return;
    setBulkBusy(true);
    // Walk the deletes sequentially — the materials endpoint has no bulk
    // route and parallel-firing N deletes risks hitting the shared rate
    // limiter + racing with the auto-refresh. Keep the tally for a
    // single summary toast at the end.
    let ok = 0;
    let failed = 0;
    for (const id of ids) {
      try {
        await api.delete(`/materials/${id}`);
        ok += 1;
      } catch (_) {
        failed += 1;
      }
    }
    if (failed === 0) {
      toast.success(`Deleted ${ok} material${ok === 1 ? "" : "s"}`);
    } else if (ok === 0) {
      toast.error(`Couldn't delete — ${failed} failed`);
    } else {
      toast.warning(`Deleted ${ok}, ${failed} failed`);
    }
    clearSelection();
    setBulkBusy(false);
    load();
  };

  const adjust = async (id, delta) => {
    try {
      await api.patch(`/materials/${id}/stock`, { delta, reason: delta > 0 ? "restock" : "used" });
      load();
    } catch (err) { toast.error("Failed"); }
  };

  const filtered = items.filter((m) => {
    const q = query.toLowerCase();
    const matchQ = !q || [m.name, m.brand, m.sku].some((x) => (x || "").toLowerCase().includes(q));
    const matchCat = !categoryFilter || m.category === categoryFilter;
    return matchQ && matchCat;
  });

  const lowStock = items.filter((m) => (m.reorder_at || 0) > 0 && (m.stock_qty || 0) <= m.reorder_at);
  const totalValue = items.reduce((s, m) => s + (Number(m.unit_price) || 0) * (Number(m.stock_qty) || 0), 0);

  // Pending Shopify orders → "Mark received" list. Auto-reload when the
  // Purchase modal closes since it usually means a new order was placed.
  const [pendingOrders, setPendingOrders] = useState([]);
  const [purchaseQuery, setPurchaseQuery] = useState("");
  const loadOrders = async () => {
    try {
      const { data } = await api.get("/shopify/orders?status=pending");
      setPendingOrders(data || []);
    } catch { setPendingOrders([]); }
  };
  useEffect(() => { loadOrders(); }, []);
  useEffect(() => { if (!purchaseOpen) loadOrders(); }, [purchaseOpen]);

  const markReceived = async (orderId) => {
    try {
      const { data } = await api.post(`/shopify/orders/${orderId}/mark-received`);
      const lineCount = data?.line_count || 0;
      toast.success(`Delivery logged — ${lineCount} line${lineCount === 1 ? "" : "s"} added to stock`);
      await Promise.all([load(), loadOrders()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't mark as received");
    }
  };

  const cancelOrder = async (orderId) => {
    try {
      await api.post(`/shopify/orders/${orderId}/cancel`);
      toast.success("Order cancelled");
      loadOrders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't cancel");
    }
  };

  const openPurchaseFor = (materialName) => {
    setPurchaseQuery(materialName || "");
    setPurchaseOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-label flex items-center gap-2"><Package className="w-3 h-3" /> Warehouse</div>
          <h1 className="font-heading text-4xl font-light tracking-tighter text-white">Inventory</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPurchaseOpen(true)}
            data-testid="inventory-purchase-material"
            className="flex items-center gap-2 bg-emerald-500 text-black px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-emerald-400 shadow-lg shadow-emerald-500/20 transition"
            title="Search Vinyl Wrapping Australia's Shopify catalogue and check out from here"
          >
            <ShoppingCart className="w-4 h-4" /> Purchase material
          </button>
          <button
            data-testid="inventory-create-button"
            onClick={openCreate}
            className="flex items-center gap-2 bg-white/5 border border-white/10 text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-white/10 transition"
            title="Log stock you already have on hand (not ordered through Shopify)"
          >
            <Plus className="w-4 h-4" /> Add material
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <GlassPanel className="!p-5" delay={0.02}>
          <div className="text-label">Total items</div>
          <div className="font-heading text-3xl text-white mt-2">{items.length}</div>
        </GlassPanel>
        <GlassPanel className="!p-5" delay={0.05}>
          <div className="text-label">Stock value</div>
          <div className="font-heading text-3xl text-white mt-2">${totalValue.toLocaleString()}</div>
        </GlassPanel>
        <GlassPanel className="!p-5" delay={0.08}>
          <div className="text-label">Low stock</div>
          <div className={`font-heading text-3xl mt-2 ${lowStock.length ? "text-amber-300" : "text-emerald-300"}`}>{lowStock.length}</div>
        </GlassPanel>
        <GlassPanel className="!p-5 hidden md:block" delay={0.1}>
          <div className="text-label">Categories</div>
          <div className="font-heading text-3xl text-white mt-2">{new Set(items.map((m) => m.category)).size}</div>
        </GlassPanel>
      </div>

      {lowStock.length > 0 && (
        <GlassPanel className="!p-5 !bg-amber-500/5 !border-amber-400/30" delay={0.12} data-testid="reorder-alert-panel">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-300 mt-1 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div>
                  <div className="text-label text-amber-300">Reorder alert</div>
                  <div className="text-sm text-slate-300 mt-1">
                    {lowStock.length} material{lowStock.length === 1 ? "" : "s"} at or below the reorder threshold — tap Purchase to jump into the Shopify catalogue pre-filtered to that item.
                  </div>
                </div>
              </div>
              <div className="mt-3 grid gap-1.5">
                {lowStock.map((m) => (
                  <div
                    key={m.id}
                    data-testid={`reorder-row-${m.id}`}
                    className="flex items-center justify-between gap-3 bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-white text-sm font-medium truncate">{m.name}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2">
                        <span>{m.stock_qty} {m.unit} on hand</span>
                        <span className="text-slate-600">·</span>
                        <span className="text-amber-300">reorder at {m.reorder_at}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => openPurchaseFor(m.name)}
                      data-testid={`reorder-purchase-${m.id}`}
                      className="flex items-center gap-1.5 bg-emerald-500 text-black text-xs font-semibold rounded-full px-3 py-1.5 hover:bg-emerald-400 transition shrink-0"
                    >
                      <ShoppingCart className="w-3 h-3" /> Purchase
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </GlassPanel>
      )}

      {/* Pending Shopify orders — mark them received to auto-log stock */}
      {pendingOrders.length > 0 && (
        <GlassPanel className="!p-0 overflow-hidden" data-testid="pending-shopify-orders-panel">
          <div className="px-5 py-4 border-b border-white/5">
            <div className="text-label flex items-center gap-2 text-emerald-300">
              <ShoppingCart className="w-3 h-3" /> Awaiting delivery
            </div>
            <h2 className="font-heading text-xl text-white mt-1">Pending Shopify orders</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Placed through WrapConsole. Tap <span className="text-emerald-300">Received</span> when the rolls arrive and we&apos;ll auto-add the quantities to your Inventory.
            </p>
          </div>
          <div className="divide-y divide-white/5">
            {pendingOrders.map((o) => {
              const totalQty = (o.lines || []).reduce((s, l) => s + (l.quantity || 0), 0);
              const totalValueAud = (o.lines || []).reduce((s, l) => s + (l.quantity || 0) * (l.price || 0), 0);
              return (
                <div key={o.id} data-testid={`pending-order-${o.id}`} className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {o.lines?.[0]?.image_url && (
                      <img src={o.lines[0].image_url} alt="" className="w-10 h-10 rounded-lg object-cover border border-white/10 shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-white text-sm font-medium truncate">
                        {(o.lines || []).map((l) => `${l.product_title}${l.variant_title ? ` (${l.variant_title})` : ""} × ${l.quantity}`).join(", ")}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{totalQty} unit{totalQty === 1 ? "" : "s"}</span>
                        {totalValueAud > 0 && <><span className="text-slate-600">·</span><span>${totalValueAud.toFixed(2)} AUD</span></>}
                        <span className="text-slate-600">·</span>
                        <span>ordered by {o.created_by_name || "someone"}</span>
                        <span className="text-slate-600">·</span>
                        <span>{new Date(o.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => cancelOrder(o.id)}
                      data-testid={`pending-cancel-${o.id}`}
                      className="px-3 py-1.5 text-xs rounded-full bg-white/[0.04] border border-white/10 text-slate-300 hover:bg-white/[0.08] transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => markReceived(o.id)}
                      data-testid={`pending-receive-${o.id}`}
                      className="px-3 py-1.5 text-xs rounded-full bg-emerald-500 text-black font-semibold hover:bg-emerald-400 transition flex items-center gap-1.5"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" /> Received
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </GlassPanel>
      )}


      {/* What needs to be ordered — job-card materials not yet stocked */}
      <GlassPanel className="!p-0 overflow-hidden" data-testid="needs-order-panel">
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-label flex items-center gap-2"><ShoppingCart className="w-3 h-3" /> Order queue</div>
            <h2 className="font-heading text-xl text-white mt-1">What needs to be ordered</h2>
            <p className="text-xs text-slate-500 mt-0.5">Materials typed on open jobs that aren&apos;t in stock yet — add them here so nothing goes on order twice.</p>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 tabular-nums">
            {needsOrder.length} item{needsOrder.length === 1 ? "" : "s"}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-white/5 text-left">
                <th className="px-5 py-3">Material</th>
                <th className="px-5 py-3">Requested by</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {needsOrder.length === 0 && (
                <tr><td colSpan={3} className="px-5 py-8 text-center text-slate-500 italic">Nothing outstanding — every material on active jobs is stocked. 🎉</td></tr>
              )}
              {needsOrder.map((row) => (
                <tr key={row.material} data-testid={`needs-order-row-${row.material}`} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                  <td className="px-5 py-3">
                    <div className="text-white font-medium">{row.material}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {row.job_count} job{row.job_count > 1 ? "s" : ""}
                      {row.sub_statuses.filter(Boolean).length > 0 && ` · ${row.sub_statuses.join(", ")}`}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      {row.jobs.slice(0, 4).map((j) => (
                        <span key={j.id} className="pill pill-muted text-[10px]">
                          {j.contact_name || j.vehicle || j.id.slice(0, 6)}
                        </span>
                      ))}
                      {row.jobs.length > 4 && (
                        <span className="text-[10px] text-slate-500">+{row.jobs.length - 4} more</span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      data-testid={`needs-order-add-${row.material}`}
                      onClick={() => openAddFromNeeded(row)}
                      className="inline-flex items-center gap-1.5 text-xs bg-white text-black px-3 py-1.5 rounded-full font-semibold hover:bg-slate-100"
                    >
                      <ArrowDown className="w-3 h-3" /> Add to inventory
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </GlassPanel>

      <div className="pt-2">
        <div className="text-label flex items-center gap-2"><Package className="w-3 h-3" /> Stocked materials</div>
        <h2 className="font-heading text-xl text-white mt-1">Inventory</h2>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <input
          data-testid="inventory-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search materials…"
          className="bg-black/40 border border-white/10 rounded-full px-4 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
        />
        <select
          data-testid="inventory-category-filter"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="bg-black/40 border border-white/10 rounded-full px-4 py-2 text-sm text-white focus:outline-none focus:border-white/30"
        >
          <option value="" className="bg-neutral-900">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c} className="bg-neutral-900">{c}</option>)}
        </select>
      </div>

      <GlassPanel className="!p-0 overflow-hidden">
        {/* Bulk-select action bar — only renders once something is ticked.
            Floats inside the table card so it reads as an in-context
            action, not a disconnected global toolbar. */}
        {selected.size > 0 && (
          <div
            className="px-5 py-3 border-b border-white/5 bg-rose-500/[0.06] flex items-center justify-between gap-3 flex-wrap"
            data-testid="inventory-bulk-bar"
          >
            <div className="flex items-center gap-2 text-sm text-rose-100">
              <CheckSquare className="w-4 h-4 text-rose-300" />
              <span><b data-testid="inventory-bulk-count">{selected.size}</b> material{selected.size === 1 ? "" : "s"} selected</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={clearSelection}
                data-testid="inventory-bulk-clear"
                className="inline-flex items-center gap-1 text-xs text-rose-100/80 hover:text-white px-3 py-1.5 rounded-full hover:bg-white/5"
              >
                <X className="w-3 h-3" /> Clear
              </button>
              <button
                onClick={bulkDelete}
                disabled={bulkBusy}
                data-testid="inventory-bulk-delete"
                className="inline-flex items-center gap-1.5 bg-rose-500 hover:bg-rose-400 text-white rounded-full px-4 py-1.5 text-xs font-semibold disabled:opacity-50"
              >
                <Trash2 className="w-3.5 h-3.5" />
                {bulkBusy ? "Deleting…" : `Delete ${selected.size}`}
              </button>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="inventory-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-white/5 text-left">
                <th className="pl-5 pr-2 py-4 w-10">
                  {/* Header checkbox — toggles the full visible set */}
                  {(() => {
                    const visibleIds = filtered.map((m) => m.id);
                    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
                    const someSelected = visibleIds.some((id) => selected.has(id)) && !allSelected;
                    return (
                      <button
                        onClick={() => toggleAllVisible(visibleIds)}
                        data-testid="inventory-select-all"
                        aria-label={allSelected ? "Deselect all" : "Select all"}
                        className="inline-flex items-center justify-center w-5 h-5 rounded-md border transition
                                   border-white/20 hover:border-white/40
                                   data-[on=true]:bg-rose-500 data-[on=true]:border-rose-400"
                        data-on={allSelected ? "true" : "false"}
                      >
                        {allSelected
                          ? <CheckSquare className="w-3.5 h-3.5 text-white" />
                          : someSelected
                            ? <Minus className="w-3 h-3 text-rose-300" />
                            : <Square className="w-3.5 h-3.5 text-slate-500" />}
                      </button>
                    );
                  })()}
                </th>
                <th className="px-5 py-4">Material</th>
                <th className="px-5 py-4">Category</th>
                <th className="px-5 py-4">Unit price</th>
                <th className="px-5 py-4">Stock</th>
                <th className="px-5 py-4">Reorder at</th>
                <th className="px-5 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-500 italic">No materials yet.</td></tr>
              )}
              {filtered.map((m) => {
                const low = (m.reorder_at || 0) > 0 && (m.stock_qty || 0) <= m.reorder_at;
                const isSelected = selected.has(m.id);
                return (
                  <tr
                    key={m.id}
                    data-testid={`inventory-row-${m.id}`}
                    className={`border-b border-white/[0.04] hover:bg-white/[0.02] ${isSelected ? "bg-rose-500/[0.04]" : ""}`}
                  >
                    <td className="pl-5 pr-2 py-3">
                      <button
                        onClick={() => toggleOne(m.id)}
                        data-testid={`inventory-select-${m.id}`}
                        aria-label={isSelected ? "Deselect" : "Select"}
                        className={`inline-flex items-center justify-center w-5 h-5 rounded-md border transition ${
                          isSelected
                            ? "bg-rose-500 border-rose-400"
                            : "border-white/20 hover:border-white/40"
                        }`}
                      >
                        {isSelected
                          ? <CheckSquare className="w-3.5 h-3.5 text-white" />
                          : <Square className="w-3.5 h-3.5 text-slate-500" />}
                      </button>
                    </td>
                    <td className="px-5 py-3">
                      <div className="text-white">{m.name}</div>
                      <div className="text-xs text-slate-500">{m.brand} {m.sku && `· ${m.sku}`}</div>
                    </td>
                    <td className="px-5 py-3"><span className="pill pill-muted">{m.category}</span></td>
                    <td className="px-5 py-3 text-white">${Number(m.unit_price).toFixed(2)}/{m.unit}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <button data-testid={`inventory-decr-${m.id}`} onClick={() => adjust(m.id, -1)} className="w-6 h-6 rounded-full bg-white/5 hover:bg-white/10 text-slate-300 flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                        <span className={`tabular-nums font-medium ${low ? "text-amber-300" : "text-white"}`}>{Number(m.stock_qty).toLocaleString()}</span>
                        <button data-testid={`inventory-incr-${m.id}`} onClick={() => adjust(m.id, +1)} className="w-6 h-6 rounded-full bg-white/5 hover:bg-white/10 text-slate-300 flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-slate-400">{m.reorder_at || "—"}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex gap-2">
                        <button data-testid={`inventory-edit-${m.id}`} onClick={() => openEdit(m)} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300"><Pencil className="w-3.5 h-3.5" /></button>
                        <button data-testid={`inventory-delete-${m.id}`} onClick={() => remove(m.id)} className="p-1.5 rounded-lg bg-white/5 hover:bg-red-500/20 text-slate-300 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </GlassPanel>

      {open && (
        <FormModal title={editing ? "Edit material" : "New material"} onClose={() => setOpen(false)} onSubmit={save}>
          <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="material-name" required />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Brand" value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} testId="material-brand" />
            <Field label="SKU" value={form.sku} onChange={(v) => setForm({ ...form, sku: v })} testId="material-sku" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <SelectField label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={CATEGORIES} testId="material-category" />
            <SelectField label="Unit" value={form.unit} onChange={(v) => setForm({ ...form, unit: v })} options={UNITS} testId="material-unit" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Unit price ($)" type="number" value={form.unit_price} onChange={(v) => setForm({ ...form, unit_price: v })} testId="material-price" />
            <Field label="Stock qty" type="number" value={form.stock_qty} onChange={(v) => setForm({ ...form, stock_qty: v })} testId="material-stock" />
            <Field label="Reorder at" type="number" value={form.reorder_at} onChange={(v) => setForm({ ...form, reorder_at: v })} testId="material-reorder" />
          </div>
          <TextArea label="Internal Shop Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testId="material-notes" />
        </FormModal>
      )}

      <PurchaseMaterialModal
        open={purchaseOpen}
        onClose={() => { setPurchaseOpen(false); setPurchaseQuery(""); }}
        defaultQuery={purchaseQuery}
      />
    </div>
  );
}
